// Tranche CA closure: resident's /me/events surface.
//
// The caller's own portal audit trail — sign-ins, guest-pass issuance,
// profile edits, revoke actions, etc. Filtered server-side to
// `actor_user_id = caller`. Does NOT show controller-side door events
// (those need to bridge through unifi_user_id — out of scope for CA).

import { useEffect, useState } from 'react';
import { Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';

interface EventRow {
  id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  created_at: string;
  request_id: string | null;
}

function actionLabel(a: string): string {
  // Human-readable action names. The audit log keeps machine-readable
  // SCREAMING_SNAKE — friendlier strings here. New audit actions should
  // be added when residents start being able to trigger them; the
  // default fall-through is intentionally readable (lowercase, spaces)
  // so an unknown action surfaces as plain prose rather than a crash.
  switch (a) {
    case 'GUEST_PASS_ISSUED':
      return 'Sponsored a guest pass';
    case 'GUEST_PASS_REVOKED':
      return 'Revoked a guest pass';
    case 'RESIDENT_INVITED':
      return 'Joined the portal';
    case 'RESIDENT_UPDATED':
      return 'Updated profile';
    case 'CREDENTIAL_ISSUED':
      return 'Credential issued';
    case 'CREDENTIAL_REVOKED':
      return 'Credential revoked';
    default:
      return a.toLowerCase().replace(/_/g, ' ');
  }
}

export function MyEventsPage() {
  const [items, setItems] = useState<EventRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ data: EventRow[] }>('/api/v1/me/events?limit=100')
      .then((res) => {
        if (!cancelled) setItems(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load events');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <span className="rounded-full bg-primary-container p-2 text-on-primary">
          <Icon name="history" />
        </span>
        <div>
          <h1 className="font-headline-sm text-headline-sm text-on-surface">
            My activity
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Recent actions you've taken in the portal. Door unlock events from
            the controller aren't included here — those will show up in a
            future release.
          </p>
        </div>
      </header>

      {error && (
        <Card>
          <CardBody>
            <p className="font-body-md text-body-md text-error" role="alert">
              {error}
            </p>
          </CardBody>
        </Card>
      )}

      {!items && !error && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-3 text-on-surface-variant"
        >
          <Spinner />
          <span>Loading your recent activity…</span>
        </div>
      )}

      {items !== null && items.length === 0 && !error && (
        <Card>
          <CardBody>
            <p className="font-body-md text-body-md text-on-surface-variant">
              No portal activity yet. As you sign in, sponsor guest passes,
              and update your profile, your actions will appear here.
            </p>
          </CardBody>
        </Card>
      )}

      {items && items.length > 0 && (
        <ol className="space-y-2">
          {items.map((ev) => (
            <li
              key={ev.id}
              className="rounded border border-outline-variant bg-surface p-3 flex items-center gap-3"
            >
              <span className="rounded-full bg-surface-container-high p-1.5 text-on-surface-variant">
                <Icon name="history" />
              </span>
              <div className="flex-1">
                <div className="font-title-sm text-title-sm text-on-surface">
                  {actionLabel(ev.action)}
                </div>
                <div className="font-body-md text-body-md text-on-surface-variant">
                  {ev.resource_type}
                  {ev.resource_id ? ` · ${ev.resource_id.slice(0, 8)}…` : ''}
                </div>
              </div>
              <time
                dateTime={ev.created_at}
                // Full toLocaleString() on one line beside the description
                // column ran 7px past a 360px screen. It keeps its single
                // line from `sm` up, and wraps below that.
                className="text-on-surface-variant text-sm sm:whitespace-nowrap"
              >
                {new Date(ev.created_at).toLocaleString()}
              </time>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
